import apiService from "./apiService";

class MagService {
  // Cache per evitare chiamate ridondanti
  static _categorieCompleteCache = null;
  static _categorieCompletePromise = null;

  /**
   * Ottiene tutte le categorie, aliquote IVA e unità di misura del magazzino
   * Questa chiamata viene effettuata una sola volta e il risultato viene cachato
   * @param {boolean} forceRefresh - Se true, forza il refresh della cache
   * @returns {Promise<Object>} Oggetto con categorie separate per l'utilizzo nei combo
   */
  async getCategorieCompleteData(forceRefresh = false) {
    try {
      // Se abbiamo già i dati in cache e non forziamo il refresh, li restituiamo
      if (!forceRefresh && MagService._categorieCompleteCache) {
        console.log('📋 Utilizzo cache per categorie complete');
        return MagService._categorieCompleteCache;
      }

      // Se c'è già una chiamata in corso, aspettiamo quella
      if (MagService._categorieCompletePromise) {
        console.log('⏳ Chiamata alle categorie già in corso, aspetto...');
        return await MagService._categorieCompletePromise;
      }

      // Creiamo la promise per la chiamata API
      MagService._categorieCompletePromise = this._fetchCategorieCompleteData();
      
      try {
        const result = await MagService._categorieCompletePromise;
        
        // Salviamo in cache il risultato
        MagService._categorieCompleteCache = result;
        
        return result;
      } finally {
        // Resettiamo la promise per permettere future chiamate
        MagService._categorieCompletePromise = null;
      }
    } catch (error) {
      console.error('Errore nel recupero delle categorie complete del magazzino:', error);
      // In caso di errore, resettiamo anche la cache
      MagService._categorieCompleteCache = null;
      MagService._categorieCompletePromise = null;
      throw error;
    }
  }

  /**
   * Metodo privato per effettuare la chiamata API
   * @private
   * @returns {Promise<Object>} Dati delle categorie complete
   */
  async _fetchCategorieCompleteData() {
    console.log('🌐 Chiamata API per categorie complete...');


    const response = await apiService('/magazzino/categorie', 'GET');
    
    // Estrae i dati dalla risposta
    const data = response.data;
    
    // Estrae le informazioni sui gruppi per ottenere le etichette dinamiche
    const gruppi = response.message?.Gruppi || [];
    
    // Crea un mapping dinamico basato sui gruppi
    const categorieComplete = {
      // Metadati sui gruppi con le etichette
      gruppiInfo: gruppi,
      
      // Dati per gruppo con etichette dinamiche
      gruppi: {},
      
      // Unità di misura (sempre fisse)
      unitaMisura: data.UNITAMISURA || [],
      
      // Aliquote IVA (sempre fisse)
      aliquoteIva: data.ALIQUOTAIVA || [],
    };
    
    // Popola dinamicamente i gruppi basandosi sulle etichette
    gruppi.forEach((gruppo, index) => {

      const numeroGruppo = index + 1;
      const etichetta = gruppo[`Gruppo${numeroGruppo}`];
      const nomeGruppo = `gruppo${numeroGruppo}`;
      
      categorieComplete.gruppi[nomeGruppo] = {
        etichetta: etichetta,
        data: data[etichetta] || [],
        categorieCount: gruppo["Categorie trovate"] || 0
      };
    });
    
    console.log('✅ Categorie complete caricate e processate');
    
    return categorieComplete;
  }

  /**
   * Ottiene i dati del Gruppo 1 (solitamente Categorie)
   * @returns {Promise<Object>} Oggetto con etichetta e dati del gruppo
   */
  async getGruppo1() {
    try {

      const categorieComplete = await this.getCategorieCompleteData();
      const gruppo1 = categorieComplete.gruppi.gruppo1 || { etichetta: "Categoria", data: [], categorieCount: 0 };
      
      // Applica trim al campo codice se presente
      const dataTrimmed = gruppo1.data.map(item => {
        if (typeof item === 'object' && item.codice) {
          return {
            ...item,
            codice: item.codice.toString().trim()
          };
        }
        return item;
      });
      
      return {
        etichetta: gruppo1.etichetta,
        data: dataTrimmed,
        categorieCount: gruppo1.categorieCount
      };
    } catch (error) {
      console.error('Errore nel recupero del Gruppo 1:', error);
      throw error;
    }
  }

  /**
   * Ottiene i dati del Gruppo 2 (solitamente Tipologie)
   * @returns {Promise<Object>} Oggetto con etichetta e dati del gruppo
   */
  async getGruppo2() {
    try {
      const categorieComplete = await this.getCategorieCompleteData();
      const gruppo2 = categorieComplete.gruppi.gruppo2 || { etichetta: "Tipologia", data: [], categorieCount: 0 };
      
      // Applica trim al campo codice se presente
      const dataTrimmed = gruppo2.data.map(item => {
        if (typeof item === 'object' && item.codice) {
          return {
            ...item,
            codice: item.codice.toString().trim()
          };
        }
        return item;
      });
      
      return {
        etichetta: gruppo2.etichetta,
        data: dataTrimmed,
        categorieCount: gruppo2.categorieCount
      };
    } catch (error) {
      console.error('Errore nel recupero del Gruppo 2:', error);
      throw error;
    }
  }

  /**
   * Ottiene i dati del Gruppo 3
   * @returns {Promise<Object>} Oggetto con etichetta e dati del gruppo
   */
  async getGruppo3() {
    try {
      const categorieComplete = await this.getCategorieCompleteData();
      const gruppo3 = categorieComplete.gruppi.gruppo3 || { etichetta: "--", data: [], categorieCount: 0 };
      
      // Applica trim al campo codice se presente
      const dataTrimmed = gruppo3.data.map(item => {
        if (typeof item === 'object' && item.codice) {
          return {
            ...item,
            codice: item.codice.toString().trim()
          };
        }
        return item;
      });
      
      return {
        etichetta: gruppo3.etichetta,
        data: dataTrimmed,
        categorieCount: gruppo3.categorieCount
      };
    } catch (error) {
      console.error('Errore nel recupero del Gruppo 3:', error);
      throw error;
    }
  }

  /**
   * Ottiene i dati del Gruppo 4
   * @returns {Promise<Object>} Oggetto con etichetta e dati del gruppo
   */
  async getGruppo4() {
    try {
      const categorieComplete = await this.getCategorieCompleteData();
      const gruppo4 = categorieComplete.gruppi.gruppo4 || { etichetta: "--", data: [], categorieCount: 0 };
      
      // Applica trim al campo codice se presente
      const dataTrimmed = gruppo4.data.map(item => {
        if (typeof item === 'object' && item.codice) {
          return {
            ...item,
            codice: item.codice.toString().trim()
          };
        }
        return item;
      });
      
      return {
        etichetta: gruppo4.etichetta,
        data: dataTrimmed,
        categorieCount: gruppo4.categorieCount
      };
    } catch (error) {
      console.error('Errore nel recupero del Gruppo 4:', error);
      throw error;
    }
  }

  /**
   * Ottiene i dati del Gruppo 5
   * @returns {Promise<Object>} Oggetto con etichetta e dati del gruppo
   */
  async getGruppo5() {
    try {
      const categorieComplete = await this.getCategorieCompleteData();
      const gruppo5 = categorieComplete.gruppi.gruppo5 || { etichetta: "--", data: [], categorieCount: 0 };
      
      // Applica trim al campo codice se presente
      const dataTrimmed = gruppo5.data.map(item => {
        if (typeof item === 'object' && item.codice) {
          return {
            ...item,
            codice: item.codice.toString().trim()
          };
        }
        return item;
      });
      
      return {
        etichetta: gruppo5.etichetta,
        data: dataTrimmed,
        categorieCount: gruppo5.categorieCount
      };
    } catch (error) {
      console.error('Errore nel recupero del Gruppo 5:', error);
      throw error;
    }
  }

  /**
   * Ottiene i dati del Gruppo 6
   * @returns {Promise<Object>} Oggetto con etichetta e dati del gruppo
   */
  async getGruppo6() {
    try {
      const categorieComplete = await this.getCategorieCompleteData();
      const gruppo6 = categorieComplete.gruppi.gruppo6 || { etichetta: "", data: [], categorieCount: 0 };
      
      // Applica trim al campo codice se presente
      const dataTrimmed = gruppo6.data.map(item => {
        if (typeof item === 'object' && item.codice) {
          return {
            ...item,
            codice: item.codice.toString().trim()
          };
        }
        return item;
      });
      
      return {
        etichetta: gruppo6.etichetta,
        data: dataTrimmed,
        categorieCount: gruppo6.categorieCount
      };
    } catch (error) {
      console.error('Errore nel recupero del Gruppo 6:', error);
      throw error;
    }
  }

  /**
   * Ottiene le informazioni su tutti i gruppi disponibili
   * @returns {Promise<Object>} Oggetto con informazioni sui gruppi
   */
  async getGruppiInfo() {
    try {
      const categorieComplete = await this.getCategorieCompleteData();
      return {
        gruppiInfo: categorieComplete.gruppiInfo,
        gruppi: categorieComplete.gruppi
      };
    } catch (error) {
      console.error('Errore nel recupero delle informazioni sui gruppi:', error);
      throw error;
    }
  }

  /**
   * Ottiene solo le unità di misura
   * @returns {Promise<Array>} Array delle unità di misura
   */
  async getUnitaMisura() {
    try {
      const categorieComplete = await this.getCategorieCompleteData();
      
      // Applica trim al campo codice di ogni unità di misura
      const unitaMisuraTrimmed = categorieComplete.unitaMisura.map(unita => {
        if (typeof unita === 'object' && unita.codice) {
          return {
            ...unita,
            codice: unita.codice.toString().trim(),
            nome: unita.nome ? unita.nome.toString().trim() : unita.nome
          };
        } else if (typeof unita === 'string') {
          return unita.trim();
        }
        return unita;
      });
      
      return unitaMisuraTrimmed;
    } catch (error) {
      console.error('Errore nel recupero delle unità di misura:', error);
      throw error;
    }
  }

  /**
   * Ottiene solo le aliquote IVA
   * @returns {Promise<Array>} Array delle aliquote IVA
   */
  async getAliquoteIva() {
    try {
      const categorieComplete = await this.getCategorieCompleteData();
      
      // Applica trim al campo codice di ogni aliquota IVA
      const aliquoteIvaTrimmed = categorieComplete.aliquoteIva.map(aliquota => {
        if (typeof aliquota === 'object' && aliquota.codice) {
          return {
            ...aliquota,
            codice: aliquota.codice.toString().trim()
          };
        }
        return aliquota;
      });
      
      return aliquoteIvaTrimmed;
    } catch (error) {
      console.error('Errore nel recupero delle aliquote IVA:', error);
      throw error;
    }
  }

  /**
   * Ottiene i dati del magazzino con filtri di ricerca
   * @param {Object} params - Parametri di ricerca (opzionali)
   * @param {string} params.searchText - Testo di ricerca (minimo 3 caratteri)
   * @param {string} params.searchField - Campo su cui cercare ('cd_min' o 'descriz')
   * @returns {Promise<Object>} Dati del magazzino filtrati
   */
  async getMagazzino(params = {}) {
    try {
      console.log('🔍 === RICERCA MAGAZZINO ===');
      console.log('📋 Parametri ricevuti:', params);
      
      // Costruisce l'URL base
      let url = '/magazzino';
      const queryParams = {};
      
      // Se ci sono parametri di ricerca, li elabora
      if (params.searchText && params.searchField) {
        const searchText = params.searchText.trim();
        const searchField = params.searchField;
        
        console.log('🔎 Testo ricerca:', searchText);
        console.log('📝 Campo ricerca:', searchField);
        console.log('📏 Lunghezza testo:', searchText.length);
        
        const upperCaseSearchText = searchText.toUpperCase();


        // Controlla che il testo abbia almeno 3 caratteri
        if (searchText.length < 3) {
          console.log('⚠️ Testo di ricerca troppo corto (minimo 3 caratteri)');
          return { data: [] }; // Ritorna array vuoto se meno di 3 caratteri
        }
        
        // Costruisce il parametro di ricerca con LIKE
        if (searchField === 'cd_min') {
          queryParams['cd_min[like]'] = `%${upperCaseSearchText}%`;
        } else if (searchField === 'descriz') {
          queryParams['descriz[like]'] = `%${upperCaseSearchText}%`;
        }
        
        console.log('🔧 Parametri query costruiti:', queryParams);
      }
      
      // Altri parametri di filtro (se presenti)
      if (params.categoria) queryParams.categoria = params.categoria;
      if (params.tipo_arti) queryParams.tipo_arti = params.tipo_arti;
      if (params.iva) queryParams.iva = params.iva;
      
      // Se ci sono parametri, li aggiunge alla query string
      if (Object.keys(queryParams).length > 0) {
        const queryString = new URLSearchParams(queryParams).toString();
        url += `?${queryString}`;
        console.log('🌐 URL finale:', url);
      } else {
        console.log('📝 Nessun filtro applicato, caricamento completo');
      }

      const response = await apiService(url, 'GET');
      
      console.log('✅ Risposta ricerca:', {
        numeroRisultati: response.data?.length || 0,
        datiPresenti: !!response.data
      });
      
      return response;
    } catch (error) {
      console.error('💥 Errore nel recupero dei dati del magazzino:', error);
      throw error;
    }
  }

  /**
   * Inserisce una nuova categoria nel magazzino
   * @param {number} numCategoria - Numero che corrisponde al tipo di categoria
   * @param {string} nomeCategoria - Nome della categoria da inserire
   * @returns {Promise<Object>} Risposta dell'inserimento
   */
  async insertCategoria(numCategoria, nomeCategoria) {
    try {
      const response = await apiService('/magazzino/categorie', 'POST', {
        numCategoria: numCategoria,
        nomeCategoria: nomeCategoria
      });
      
      return response.data;
    } catch (error) {
      console.error('Errore nell\'inserimento della nuova categoria:', error);
      throw error;
    }
  }


  /**
   * elimina una categoria nel magazzino
   * @param {number} numCategoria - Numero che corrisponde al tipo di categoria
   * @param {string} codCategoria - codice della categoria da inserire
   * @returns {Promise<Object>} Risposta dell'inserimento
   */
  async deleteCategoria(numCategoria, codCategoria) {
    try {
      const response = await apiService('/magazzino/categorie', 'DELETE', {
        numCategoria: numCategoria,
        codCategoria: codCategoria
      });
      
      return response.data;
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      throw error;
    }
  }


  /**
   * Inserisce un nuovo prodotto nel magazzino
   * @param {Object} prodottoData - Dati del prodotto da inserire
   * @returns {Promise<Object>} Risposta dell'inserimento
   */
  async insertMagazzino(prodottoData) {
    try {
      console.log('🆕 === INSERIMENTO NUOVO ARTICOLO ===');
      console.log('📋 Dati ricevuti:', prodottoData);
      console.log('🔢 Numero campi:', Object.keys(prodottoData).length);
      
      const {
        // Campi principali obbligatori
        cd_min,
        descriz,
        iva,
        unita_m,
        mag_extlc_mag,
        
        // Campi identificativi
        barcode,
        cod_for,
        codice_alt,
        barcode_clie,
        cod_bancale,
        cod_eti,
        
        // Prezzi e costi
        cost_medio,
        costo_u,
        costo_futu,
        costo_lordo,
        costo_lavo,
        listino1,
        listino2,
        list_saldi,
        
        // Sconti e ricarichi
        sconto1,
        sconto2,
        
        // Quantità e giacenze
        giacenza,
        impegnato,
        ordinato,
        scort_m,
        qtamin,
        qtaminima,
        qtarange,
        orebloccoprod,
        
        // Dimensioni e pesi
        peso_lo,
        volumes,
        confezione,
        altezza,
        larghezza,
        spessore,
        area,
        pm,
        pos,
        
        // Categorie e gruppi
        colore,
        tipo_arti,
        ngruppo3,
        ncolore,
        gruppo5,
        gruppo6,
        mgruppo7,
        
        // Posizione e logistica
        posizione,
        pag_cat,
        ubicazione,
        
        // Date
        data_u_scr,
        data_aq,
        data_inizzio,
        data_fine,
        scade_abb,
        data_foto,
        data_tecnica,
        data_sicurezza,
        
        // Contabilità
        contro,
        contoa,
        controc,
        prov,
        
        // Caratteristiche tecniche
        tono,
        calibro,
        mq_scat,
        mq_pal,
        gomma,
        euromt,
        tipo_cod,
        macchina,
        cod_dogana,
        
        // Note e descrizioni
        note_est,
        descriz_lang,
        scheda_tec,
        scheda_sic,
        
        // References
        figliopa,
        clie_rif,
        padre,
        serie,
        
        // Immagini
        foto,
        newfoto,
        newfoto2,
        urlimmagine,
        
        // Flag booleani (0/1)
        ordinamento,
        arti_o_lavo,
        inv,
        duplicab,
        cal_inv,
        no_scontrino,
        tracciabi,
        ricetta,
        saldi,
        su_web,
        su_flash,
        stampuni,
        art_a_peso,
        no_scade,
        packorari,
        front_da_stampare,
        lavo1,
        sche_tec,
        c_aee,
        tipotes,
        sottocomp_finiti,
        d1,
        
        // Altri parametri
        ...altriParametri
      } = prodottoData;

      console.log('🔧 === PROCESSAMENTO MAG_EXTLC_MAG ===');
      console.log('📋 Valore originale mag_extlc_mag:', mag_extlc_mag);
      
      // Se mag_extlc_mag è undefined o null, lo compiliamo con i dati di default
      let processedMagExtlcMag = mag_extlc_mag;
      
      if (!mag_extlc_mag || mag_extlc_mag === null || mag_extlc_mag === undefined) {
        console.log('⚠️ mag_extlc_mag è vuoto, creo dati di default...');
        
        processedMagExtlcMag = [
          {
            "codlis": "1",
            "codarti": cd_min,
            "prezzo": "0",
            "sc1": "0",
            "sc2": "0",
            "sc3": "0",
            "ricar": "0"
          },
          {
            "codlis": "2",
            "codarti": cd_min,
            "prezzo": "0",
            "sc1": "0",
            "sc2": "0",
            "sc3": "0",
            "ricar": "0"
          }
        ];
        
        console.log('✅ Dati di default creati:', processedMagExtlcMag);
      } else {
        console.log('✅ mag_extlc_mag esistente utilizzato');
      }

      // Verifica campi obbligatori
      console.log('🔍 === VERIFICA CAMPI OBBLIGATORI ===');
      const campiObbligatori = { cd_min, descriz, iva, unita_m };
      console.log('📋 Campi obbligatori:', campiObbligatori);
      
      const campiMancanti = [];
      if (!cd_min) campiMancanti.push('cd_min');
      if (!descriz) campiMancanti.push('descriz');
      if (!iva) campiMancanti.push('iva');
      if (!unita_m) campiMancanti.push('unita_m');
      
      if (campiMancanti.length > 0) {
        console.error('❌ Campi obbligatori mancanti:', campiMancanti);
        throw new Error(`Campi obbligatori mancanti: ${campiMancanti.join(', ')}`);
      }

      console.log('✅ Tutti i campi obbligatori sono presenti');
      console.log('🔧 === VALORI FISSI APPLICATI ===');
      console.log('📋 cod_for: 0001 (fisso)');
      console.log('📋 arti_o_lavo: 1 (fisso)');

      // Prepara i parametri per la query string
      const queryParams = {
        cd_min,
        descriz,
        iva,
        unita_m,
        // Usa sempre processedMagExtlcMag (mai undefined)
        mag_extlc_mag: JSON.stringify(processedMagExtlcMag),
        // Valori fissi per nuovi articoli
        cod_for: '0001',
        arti_o_lavo: 1,
        
        // Aggiunge tutti gli altri parametri se presenti (esclusi quelli già fissi)
        ...(barcode && { barcode }),
        ...(codice_alt && { codice_alt }),
        ...(barcode_clie && { barcode_clie }),
        ...(cod_bancale && { cod_bancale }),
        ...(cod_eti && { cod_eti }),
        
        ...(cost_medio && { cost_medio }),
        ...(costo_u && { costo_u }),
        ...(costo_futu && { costo_futu }),
        ...(costo_lordo && { costo_lordo }),
        ...(costo_lavo && { costo_lavo }),
        ...(listino1 && { listino1 }),
        ...(listino2 && { listino2 }),
        ...(list_saldi && { list_saldi }),
        
        ...(sconto1 && { sconto1 }),
        ...(sconto2 && { sconto2 }),
        
        ...(giacenza && { giacenza }),
        ...(impegnato && { impegnato }),
        ...(ordinato && { ordinato }),
        ...(scort_m && { scort_m }),
        ...(qtamin && { qtamin }),
        ...(qtaminima && { qtaminima }),
        ...(qtarange && { qtarange }),
        ...(orebloccoprod !== undefined && orebloccoprod !== null
          ? { orebloccoprod: Number(orebloccoprod) || 0 }
          : {}),
        
        ...(peso_lo && { peso_lo }),
        ...(volumes && { volumes }),
        ...(confezione && { confezione }),
        ...(altezza && { altezza }),
        ...(larghezza && { larghezza }),
        ...(spessore && { spessore }),
        ...(area && { area }),
        ...(pm && { pm }),
        ...(pos && { pos }),
        
        ...(colore && { colore }),
        ...(tipo_arti && { tipo_arti }),
        ...(ngruppo3 && { ngruppo3 }),
        ...(ncolore && { ncolore }),
        ...(gruppo5 && { gruppo5 }),
        ...(gruppo6 && { gruppo6 }),
        ...(mgruppo7 && { mgruppo7 }),
        
        ...(posizione && { posizione }),
        ...(pag_cat && { pag_cat }),
        
        ...(data_u_scr && { data_u_scr }),
        ...(data_aq && { data_aq }),
        ...(data_inizzio && { data_inizzio }),
        ...(data_fine && { data_fine }),
        ...(scade_abb && { scade_abb }),
        ...(data_foto && { data_foto }),
        ...(data_tecnica && { data_tecnica }),
        ...(data_sicurezza && { data_sicurezza }),
        
        ...(contro && { contro }),
        ...(contoa && { contoa }),
        ...(controc && { controc }),
        ...(prov !== undefined && { prov }),
        
        ...(tono && { tono }),
        ...(calibro && { calibro }),
        ...(mq_scat && { mq_scat }),
        ...(mq_pal && { mq_pal }),
        ...(gomma && { gomma }),
        ...(euromt && { euromt }),
        ...(tipo_cod && { tipo_cod }),
        ...(macchina && { macchina }),
        ...(cod_dogana && { cod_dogana }),
        
        ...(note_est && { note_est }),
        ...(descriz_lang && { descriz_lang }),
        ...(scheda_tec && { scheda_tec }),
        ...(scheda_sic && { scheda_sic }),
        
        ...(figliopa !== undefined && { figliopa }),
        ...(clie_rif && { clie_rif }),
        ...(padre && { padre }),
        ...(serie && { serie }),
        
        ...(foto && { foto }),
        ...(newfoto && { newfoto }),
        ...(newfoto2 && { newfoto2 }),
        ...(urlimmagine && { urlimmagine }),
        
        // Flag booleani (0/1) - esclusi quelli già fissi
        ...(ordinamento !== undefined && { ordinamento }),
        ...(inv !== undefined && { inv }),
        ...(duplicab !== undefined && { duplicab }),
        ...(cal_inv !== undefined && { cal_inv }),
        ...(no_scontrino !== undefined && { no_scontrino }),
        ...(tracciabi !== undefined && { tracciabi }),
        ...(ricetta !== undefined && { ricetta }),
        ...(saldi !== undefined && { saldi }),
        ...(su_web !== undefined && { su_web }),
        ...(su_flash !== undefined && { su_flash }),
        ...(stampuni !== undefined && { stampuni }),
        ...(art_a_peso !== undefined && { art_a_peso }),
        ...(no_scade !== undefined && { no_scade }),
        ...(packorari !== undefined && { packorari }),
        ...(front_da_stampare !== undefined && { front_da_stampare }),
        ...(lavo1 !== undefined && { lavo1 }),
        ...(sche_tec !== undefined && { sche_tec }),
        ...(c_aee !== undefined && { c_aee }),
        ...(tipotes !== undefined && { tipotes }),
        ...(sottocomp_finiti !== undefined && { sottocomp_finiti }),
        ...(d1 !== undefined && { d1 }),
        
        ...altriParametri
      };
      
      console.log('🧹 === PREPARAZIONE DATI PER BODY ===');
      console.log('📊 Parametri preparati:', Object.keys(queryParams));
      console.log('📋 Valori parametri:', queryParams);
      console.log('🔢 Numero parametri:', Object.keys(queryParams).length);

      // Invece di usare query string, usiamo il body come per updateMagazzino
      const url = `/magazzino/nuovo`;
      
      console.log('📡 === CHIAMATA API INSERT ===');
      console.log('🌐 URL:', url);
      console.log('� Dati nel body:', queryParams);
      console.log('� Dimensione dati:', JSON.stringify(queryParams).length, 'bytes');

      const response = await apiService(url, 'PUT', queryParams);
      
      console.log('✅ === RISPOSTA INSERT COMPLETATA ===');
      console.log('📤 Risposta server:', response);
      
      return response.data;
    } catch (error) {

      console.error('💥 === ERRORE INSERIMENTO ARTICOLO ===');
      console.error('❌ Errore completo:', error);
      console.error('📜 Stack trace:', error.stack);
      console.error('🌐 Response error:', error.response?.data);
      console.error('📊 Status code:', error.response?.status);
      console.error('📋 Headers:', error.response?.headers);
      console.error('📦 Dati che hanno causato errore:', prodottoData);
      
      // Log dettagliato per errore 400
      if (error.response?.status === 400) {
        console.error('🚨 === ERRORE 400 BAD REQUEST ===');
        console.error('📋 Messaggio server:', error.response?.data?.message);
        console.error('📝 Errori specifici:', error.response?.data?.errors);
        console.error('🔗 URL della richiesta:', error.config?.url);
        console.error('📦 Data inviata:', error.config?.data);
        console.error('🌐 Method:', error.config?.method);
      }
      
      // Log specifico per errori di query string troppo lunga
      if (error.response?.status === 414) {
        console.error('⚠️ ERRORE 414: Query string troppo lunga - considerare POST invece di PUT con query');
      }
      
      console.error('Errore nell\'inserimento del nuovo prodotto nel magazzino:', error);
      throw error;
    }
  }

  /**
   * Modifica un articolo esistente nel magazzino
   * @param {string} cd_min - Codice del prodotto da modificare
   * @param {Object} prodottoData - Dati del prodotto da aggiornare
   * @returns {Promise<Object>} Risposta della modifica
   */
  async updateMagazzino(cd_min, prodottoData) {
    try {

      console.log('🔄 UpdateMagazzino chiamato con:', {
        cd_min,
        prodottoData: Object.keys(prodottoData),
        dataLength: Object.keys(prodottoData).length
      });

      // Validation dei parametri
      if (!cd_min) {
        throw new Error('Codice articolo (cd_min) è obbligatorio');
      }

      if (!prodottoData || typeof prodottoData !== 'object') {
        throw new Error('prodottoData deve essere un oggetto valido');
      }

      // Pulizia dei dati - rimuove valori undefined/null e campi vuoti
      const cleanedData = {};
      Object.keys(prodottoData).forEach(key => {
        const value = prodottoData[key];
        if (value !== undefined && value !== null && value !== '') {
          cleanedData[key] = value;
        }
      });

      console.log('🧹 Dati puliti:', {
        original: Object.keys(prodottoData).length,
        cleaned: Object.keys(cleanedData).length,
        removed: Object.keys(prodottoData).length - Object.keys(cleanedData).length
      });

      // Log variabili in scrittura (incluso su_flash)
      console.log('📝 Variabili che sto scrivendo:', cleanedData);
      console.log('📝 su_flash presente:', 'su_flash' in cleanedData, '→ valore:', cleanedData.su_flash);

      // Costruisce l'URL con solo il codice prodotto
      const url = `/magazzino/${cd_min}`;

      console.log('📡 Invio richiesta update a:', url);
      console.log('📦 Dati inviati nel body:', cleanedData);

      // Invia tutti i dati del prodotto nel body della chiamata POST
      const response = await apiService(url, 'POST', cleanedData);
      
      console.log('✅ Risposta server update:', response);
      return response.data;
    } catch (error) {
      console.error(`❌ Errore nella modifica dell'articolo ${cd_min}:`, error);
      console.error('📋 Dati che hanno causato l\'errore:', prodottoData);
      
      // Se è un errore di rete o server, aggiungi più dettagli
      if (error.response) {
        console.error('📡 Risposta server:', error.response.data);
        console.error('📡 Status:', error.response.status);
      }
      
      throw error;
    }
  }

  /**
   * Ottiene i dettagli di un singolo articolo del magazzino
   * @param {string} cd_min - Codice del prodotto da recuperare
   * @param {string} cod_clie - Codice cliente per prezzi personalizzati (opzionale)
   * @returns {Promise<Object>} Dettagli completi dell'articolo
   */
  async getArticolo(cd_min, cod_clie = null) {
    try {
      // Costruisce l'URL con il codice prodotto
      let url = `/magazzino/${cd_min}`;
      
      // Se è specificato il codice cliente, lo aggiunge all'URL
      if (cod_clie) {
        url += `/${cod_clie}`;
      }

      const response = await apiService(url, 'GET');
      return response.data;
    } catch (error) {
      console.error(`Errore nel recupero dell'articolo ${cd_min}:`, error);
      throw error;
    }
  }

  /**
   * Elimina un articolo dal magazzino
   * @param {string} cd_min - Codice del prodotto da eliminare
   * @returns {Promise<Object>} Risposta dell'eliminazione
   */
  async deleteMagazzino(cd_min) {
    try {
      const response = await apiService(`/magazzino/${cd_min}`, 'DELETE');
      
      return response.data;
    } catch (error) {
      console.error(`Errore nell'eliminazione dell'articolo ${cd_min}:`, error);
      throw error;
    }
  }
}

export default new MagService();
